import axios from "axios";
import { API_URL } from "../config";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import api from "./api";

function createPublicacaoError(message, status, cause) {
  const error = new Error(message);
  if (status != null) error.status = status;
  if (cause != null) error.cause = cause;
  return error;
}

function rethrowPublicacaoError(error, fallbackMessage) {
  if (error.response?.status === 429) {
    throw createPublicacaoError(
      "Muitas consultas. Tente novamente mais tarde.",
      429,
      error,
    );
  }

  const message = getApiErrorMessage(error, fallbackMessage);
  throw createPublicacaoError(message, error.response?.status, error);
}

function buildPedidoParams({
  cnpj,
  nome,
  tipoLicencaId,
  dataInicio,
  dataFim,
  pageNumber = 1,
  pageSize = 10,
} = {}) {
  const params = { pageNumber, pageSize };
  if (cnpj) params.cnpj = cnpj;
  if (nome) params.nome = nome;
  if (tipoLicencaId) params.tipoLicencaId = tipoLicencaId;
  if (dataInicio) params.dataInicio = dataInicio;
  if (dataFim) params.dataFim = dataFim;
  return params;
}

const publicacaoService = {
  criar: async (solicitacaoDeLicencaId, isAgricultorFamiliarOuBaixaRenda, comprovanteFile) => {
    const formData = new FormData();
    formData.append("SolicitacaoDeLicencaId", solicitacaoDeLicencaId);
    formData.append("IsAgricultorFamiliarOuBaixaRenda", isAgricultorFamiliarOuBaixaRenda);
    if (comprovanteFile) {
      formData.append("ComprovanteAgricultorFamiliarOuBaixaRenda", comprovanteFile);
    }

    const response = await api.post("Publicacao", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.data;
  },

  criarPedido: async ({
    nomeOuRazaoSocial,
    cnpj,
    tipoLicencaId,
    textoTipoLicenca,
    atividade,
    endereco,
    modelo,
    numeroProcessoEmTramite,
  }) => {
    const response = await api.post("Publicacao/Pedido", {
      nomeOuRazaoSocial,
      cnpj,
      tipoLicencaId,
      textoTipoLicenca,
      atividade,
      endereco,
      modelo,
      numeroProcessoEmTramite,
    });
    return response.data.data;
  },

  listarModelosPedido: async () => {
    const response = await api.get("Publicacao/ModelosPedido");
    return response.data.data || [];
  },

  listarModelosRecebimento: async () => {
    const response = await api.get("Publicacao/ModelosRecebimento");
    return response.data.data || [];
  },

  listarPedidos: async (filtros = {}) => {
    const response = await api.get("Publicacao/Pedido", {
      params: buildPedidoParams(filtros),
    });
    return response.data;
  },

  listarMeusPedidos: async (filtros = {}) => {
    const response = await api.get("Publicacao/Pedido/Meus", {
      params: buildPedidoParams(filtros),
    });
    return response.data;
  },

  baixarComprovante: async (id) => {
    const response = await api.get(`Publicacao/${id}/comprovante`, {
      responseType: "blob",
    });
    return response.data;
  },

  obterPorId: async (id) => {
    const response = await api.get(`Publicacao/${id}`);
    return response.data.data;
  },

  obterPorSolicitacao: async (solicitacaoDeLicencaId) => {
    const response = await api.get(`Publicacao/solicitacao/${solicitacaoDeLicencaId}`);
    return response.data.data;
  },

  listarPendentesAnalista: async (pageNumber = 1, pageSize = 10) => {
    const response = await api.get("Publicacao/Analista/Pendentes", {
      params: { pageNumber, pageSize },
    });
    return response.data;
  },

  atualizarStatus: async (id, status) => {
    const response = await api.put(`Publicacao/${id}/status`, { status });
    return response.data.data;
  },

  efetuar: async (id, modelo) => {
    const response = await api.post(`Publicacao/${id}/efetuar`, { modelo });
    return response.data.data;
  },

  baixarComprovanteAgricultorFamiliar: async (id) => {
    const response = await api.get(`Publicacao/${id}/comprovante-agricultor-familiar`, {
      responseType: "blob",
    });
    return response.data;
  },

  listarPublicas: async ({ busca = "", tipoLicencaId = null, pageNumber = 1, pageSize = 10 } = {}) => {
    const params = { pageNumber, pageSize };
    if (busca) params.busca = busca;
    if (tipoLicencaId) params.tipoLicencaId = tipoLicencaId;

    try {
      const response = await axios.get(`${API_URL}Publicacao/publicas`, { params });
      return response.data;
    } catch (error) {
      rethrowPublicacaoError(error, "Erro ao listar publicações.");
    }
  },

  obterPublicaPorId: async (id) => {
    try {
      const response = await axios.get(`${API_URL}Publicacao/publicas/${id}`);
      return response.data.data;
    } catch (error) {
      rethrowPublicacaoError(error, "Erro ao obter publicação.");
    }
  },

  listarTiposProcesso: async () => {
    try {
      const response = await axios.get(`${API_URL}Publicacao/publicas/tipos-processo`);
      return response.data.data || [];
    } catch (error) {
      rethrowPublicacaoError(error, "Erro ao listar tipos de processo.");
    }
  },
};

export default publicacaoService;
